import React, { useState, useEffect } from 'react';
import { Droplet, Map, FileText, ExternalLink, Download, Check, ShieldCheck, HelpCircle, ArrowRight } from 'lucide-react';
import { WATER_ANALYSES, IMAGES, DOCUMENTS, USEFUL_LINKS } from '../data';

export default function Information() {
  const [activeAnalysis, setActiveAnalysis] = useState(0);
  const [analyses, setAnalyses] = useState(WATER_ANALYSES);

  useEffect(() => {
    async function fetchAnalyses() {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/vandanalyse');
        if (!response.ok) throw new Error('Failed to fetch water analyses');
        const data = await response.json();
        
        if (Array.isArray(data)) {
          setAnalyses(prev => prev.map(item => {
            const cmsMatch = data.find(cmsItem => {
              const cmsTitle = cmsItem.title?.da || '';
              const cmsSlug = cmsItem.slug?.da || '';
              return cmsTitle.toLowerCase() === item.site.toLowerCase() || 
                     cmsSlug.toLowerCase() === item.site.toLowerCase();
            });
            
            if (cmsMatch && cmsMatch.download && cmsMatch.download[0]) {
              return {
                ...item,
                pdfUrl: cmsMatch.download[0]
              };
            }
            return item;
          }));
        }
      } catch (error) {
        console.error('Error fetching water analyses from CMS:', error);
      }
    }
    fetchAnalyses();
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center border-b border-slate-200 pb-12 mb-12">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl bg-slate-100 shadow-lg h-48 sm:h-64 lg:h-56">
              <img
                src={IMAGES.waterworksDetail}
                alt="Grindsted Vandværk detalje"
                className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-102"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Information & Vandkvalitet
            </h1>
            <p className="mt-4 text-lg text-slate-500 leading-relaxed">
              Her kan du læse om vores vandkvalitet, se de seneste vandanalyser, undersøge forsyningsområdet samt hente takstblade, vedtægter og regulativer.
            </p>
          </div>
        </div>

        {/* 1. Om Vandet & Vandanalyser */}
        <section className="mb-16 scroll-mt-24" id="vandet">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 space-y-6">
              <div className="flex items-center gap-2.5">
                <div className="h-1.5 w-8 rounded-full bg-black" />
                <span className="text-xs font-bold uppercase tracking-wider text-black">Vandkvalitet</span>
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">
                Vandet i Grindsted
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Vores vigtigste opgave er at levere rent og velsmagende drikkevand til alle forbrugere. Vandet hentes op fra vores lokale boringer og gennemgår en simpel iltnings- og filtreringsproces på vandværket, inden det distribueres ud til forbrugerne.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
                  <span className="block text-xs font-bold text-slate-400 uppercase">Hårdhedsgrad</span>
                  <span className="block mt-1 text-2xl font-extrabold text-black">Ca. 8-10 °dH</span>
                  <span className="block mt-1 text-xs text-slate-500">Blødt til middelshårdt vand. Perfekt til husholdningsmaskiner.</span>
                </div>
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
                  <span className="block text-xs font-bold text-slate-400 uppercase">Kvalitetssikring</span>
                  <span className="block mt-1 text-2xl font-extrabold text-emerald-600">100% Godkendt</span>
                  <span className="block mt-1 text-xs text-slate-500">Løbende uafhængige kontroller sikrer, at grænseværdier overholdes.</span>
                </div>
              </div>

              <div className="bg-transparent border border-black rounded-2xl p-6 space-y-3">
                <h4 className="font-bold text-black flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-black" />
                  Ingen overskridelser af grænseværdier
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Vi tester regelmæssigt for bakterier, pesticider, tungmetaller og andre uønskede stoffer. Alle prøver viser, at vandet fra Grindsted Vandværk er af yderst høj kvalitet og fuldstændig sikkert at drikke direkte fra hanen.
                </p>
              </div>
            </div>

            {/* Interactive Water Analysis Reports */}
            <div className="lg:col-span-6 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Seneste Vandanalyser</h3>
              <p className="text-sm text-slate-500 mb-6">
                Vandværket har to primære kildeområder. Vælg et område nedenfor for at se den seneste officielle analyserapport.
              </p>

              {/* Tabs */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-6">
                {analyses.map((analysis, index) => (
                  <button
                    key={analysis.site}
                    onClick={() => setActiveAnalysis(index)}
                    className={`flex-1 text-center py-2.5 text-sm font-bold rounded-lg transition-all ${
                      activeAnalysis === index
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    }`}
                  >
                    {analysis.site}
                  </button>
                ))}
              </div>

              {/* Active Tab Content */}
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  <div className="flex-1 space-y-3">
                    <h4 className="font-bold text-slate-900 text-lg">Boring: {analyses[activeAnalysis]?.site}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {analyses[activeAnalysis]?.description} Rapporten indeholder målinger af mikrobiologiske parametre, uorganiske sporstoffer samt fysisk-kemiske forhold.
                    </p>
                    <div className="pt-2">
                      <a
                        href={analyses[activeAnalysis]?.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-500 transition-all shadow-sm"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Hent fuld PDF-rapport
                      </a>
                    </div>
                  </div>
                  <div className="w-full sm:w-40 shrink-0 border border-slate-200 rounded-xl overflow-hidden shadow-inner bg-slate-50">
                    <img
                      src={analyses[activeAnalysis]?.imageUrl}
                      alt={`Analyse rapport for ${analyses[activeAnalysis]?.site}`}
                      className="w-full h-auto object-contain max-h-48 p-2"
                    />
                  </div>
                </div>

                {/* Photo of the site */}
                <div className="border-t border-slate-100 pt-6">
                  <span className="block text-xs font-bold text-slate-400 uppercase mb-3">Billede fra kildeområdet</span>
                  <div className="relative h-48 rounded-2xl overflow-hidden bg-slate-100">
                    <img
                      src={analyses[activeAnalysis]?.photoUrl}
                      alt={analyses[activeAnalysis]?.site}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent" />
                    <span className="absolute bottom-4 left-4 text-xs font-bold text-white bg-slate-900/60 px-2.5 py-1 rounded-md backdrop-blur-xs">
                      Boring ved {analyses[activeAnalysis]?.site}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. KlimaID - Følg dit forbrug */}
        <section className="mb-16 scroll-mt-24" id="klimaid">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7 space-y-6">
                <div className="flex items-center gap-2.5">
                  <div className="h-1.5 w-8 rounded-full bg-black" />
                  <span className="text-xs font-bold uppercase tracking-wider text-black">Forbrug & App</span>
                </div>
                
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">
                    Følg dit forbrug med vores nye app KlimaID
                  </h2>
                  <p className="mt-3 text-base sm:text-lg font-medium text-slate-800">
                    Vil du følge dit [vandforbrug/varmeforbrug] så har vi en god nyhed!
                  </p>
                  <p className="mt-2 text-slate-600 leading-relaxed">
                    Du kan nu downloade vores app, KlimaID, og få fuld kontrol over dit [vandforbrug/varmeforbrug].
                  </p>
                </div>

                <div className="pt-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4">
                    Med KlimaID får du adgang til en række praktiske funktioner:
                  </h3>
                  <ul className="space-y-3.5">
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Overblik over dit forbrug:</strong> Se timedata for både [vand/fjernvarme] og el
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Få besked i appen ved brud eller lækage:</strong> Hvis du aktiverer notifikationer i appen og måleren registrerer noget besynderligt, modtager du en alarm i appen.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Sammenlign dit forbrug:</strong> Tjek, hvordan dit forbrug matcher lignende husstande – baseret på oplysninger som husstandsstørrelse.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Beskeder fra os:</strong> Modtag vigtige meddelelser direkte fra forsyningen.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Nyheder lige ved hånden:</strong> Følg med i alt det nyeste fra os.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Se priser:</strong> Få fuldt overblik over dine udgifter.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Din økonomi samlet ét sted:</strong> Tjek dit samlede forbrug i kroner og ører på tværs af forsyninger.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-slate-900">Skræddersyede analyser:</strong> Få specifikke indsigter for netop din husstand.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Important notice */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-2 text-sm text-slate-600 leading-relaxed">
                  <p>
                    Vi anbefaler, at du løbende holder øje med forbruget på din fysiske måler, da der kan forekomme udfald, som bevirker, at data ikke bliver vist i appen.
                  </p>
                  <p className="font-semibold text-slate-900">
                    Vær opmærksom på, at det altid er dit eget ansvar at holde øje med forbruget.
                  </p>
                </div>

                {/* Spar penge callout */}
                <div className="border-l-4 border-black pl-4 py-1 space-y-2">
                  <h4 className="font-bold text-slate-900 text-base">
                    Spar penge – og hjælp os med at hjælpe dig!
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Når du holder øje med dit forbrug, kan du hurtigt opdage, hvis noget ikke fungerer optimalt – og det kan spare dig penge. Samtidig gør det os i stand til at hjælpe dig bedre og hurtigere, hvis der opstår problemer.
                  </p>
                </div>

                {/* Closing CTA */}
                <div className="bg-slate-900 text-white rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="font-bold text-sm sm:text-base text-center sm:text-left">
                    Hop med på KlimaID – og få styr på både [dråber/kWh] og kroner!
                  </p>
                  <a
                    href="https://klimaid.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-900 px-5 py-2.5 text-xs font-bold hover:bg-slate-100 transition shadow-sm shrink-0"
                  >
                    <span>Læs mere på klimaid.dk</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>

              {/* Right column - Graphic */}
              <div className="lg:col-span-5 flex flex-col items-center">
                <div className="relative w-full max-w-sm rounded-2xl overflow-hidden border border-slate-200 shadow-md bg-slate-100">
                  <img
                    src="/cdn-cgi/image/width=800,quality=85,format=auto/_assets/image.png"
                    alt="KlimaID app information og download"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="mt-3 text-xs text-slate-500 text-center">
                  Hent KlimaID i App Store eller Google Play og scan QR-koden for at komme i gang.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Forsyningsområde */}
        <section className="mb-16 scroll-mt-24" id="forsyningsomraade">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5 space-y-6">
                <div className="flex items-center gap-2.5">
                  <div className="h-1.5 w-8 rounded-full bg-black" />
                  <span className="text-xs font-bold uppercase tracking-wider text-black">Område</span>
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">
                  Vores Forsyningsområde
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Grindsted Vandværk forsyner husstande, landbrug og erhverv i Grindsted og de omkringliggende landområder i overensstemmelse med gældende kommunale planer.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="h-5 w-5 text-black shrink-0" />
                    <span>Dækker hele Grindsted by og opland</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="h-5 w-5 text-black shrink-0" />
                    <span>Tilsynsførende myndighed: Aalborg Kommune</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="h-5 w-5 text-black shrink-0" />
                    <span>Stabil forsyning med højt tryk og høj leveringssikkerhed</span>
                  </div>
                </div>
              </div>

              {/* Supply Map Images */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <span className="block text-xs font-bold text-slate-400 uppercase text-center">Forsyningsområde (Kort 1)</span>
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm bg-slate-50">
                    <a href={IMAGES.supplyMap} target="_blank" rel="noopener noreferrer" className="group block relative">
                      <img
                        src={IMAGES.supplyMap}
                        alt="Forsyningsområde kort 1"
                        className="w-full h-64 object-cover transition duration-300 group-hover:scale-102"
                      />
                      <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                        <span className="bg-white text-slate-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">Se stort kort</span>
                      </div>
                    </a>
                  </div>
                </div>
                <div className="space-y-2">
                  <span className="block text-xs font-bold text-slate-400 uppercase text-center">Forsyningsområde (Kort 2)</span>
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm bg-slate-50">
                    <a href={IMAGES.supplyMapAlt} target="_blank" rel="noopener noreferrer" className="group block relative">
                      <img
                        src={IMAGES.supplyMapAlt}
                        alt="Forsyningsområde kort 2"
                        className="w-full h-64 object-cover transition duration-300 group-hover:scale-102"
                      />
                      <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                        <span className="bg-white text-slate-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow-md">Se stort kort</span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Takster, Vedtægter & Regulativ */}
        <section className="mb-16 scroll-mt-24" id="dokumenter">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-black">Dokumenter</span>
            <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">
              Takstblad, Vedtægter & Regulativer
            </h2>
            <p className="mt-3 text-slate-500">
              Her kan du hente og læse de officielle dokumenter for Grindsted Vandværk. Alle dokumenter åbnes i PDF-format.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Takstblad */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition">
              <div className="space-y-4">
                <FileText className="h-8 w-8 text-black" />
                <h3 className="text-lg font-bold text-slate-900">{DOCUMENTS.takstblad.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {DOCUMENTS.takstblad.description}
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <a
                  href={DOCUMENTS.takstblad.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition"
                >
                  Vis takstblad (PDF)
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Regulativ */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition">
              <div className="space-y-4">
                <FileText className="h-8 w-8 text-black" />
                <h3 className="text-lg font-bold text-slate-900">{DOCUMENTS.regulativ.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {DOCUMENTS.regulativ.description}
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <a
                  href={DOCUMENTS.regulativ.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition"
                >
                  Læs regulativ (PDF)
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Vedtægter */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition">
              <div className="space-y-4">
                <FileText className="h-8 w-8 text-black" />
                <h3 className="text-lg font-bold text-slate-900">{DOCUMENTS.vedtaegter.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {DOCUMENTS.vedtaegter.description}
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <a
                  href={DOCUMENTS.vedtaegter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition"
                >
                  Læs vedtægter (PDF)
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Nyttige Links */}
        <section className="scroll-mt-24" id="links">
          <div className="bg-blue-950 text-white rounded-3xl p-8 sm:p-12 shadow-xl">
            <div className="max-w-3xl">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-400">Ressourcer</span>
              <h2 className="mt-2 text-2xl font-extrabold sm:text-3xl">Nyttige Links & Samarbejdspartnere</h2>
              <p className="mt-3 text-slate-300">
                Vi samarbejder tæt med myndigheder og brancheorganisationer for at sikre, at vi altid overholder de nyeste standarder og lovkrav. Besøg deres hjemmesider for yderligere information.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {USEFUL_LINKS.map((link) => (
                <a
                  key={link.title}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-white border border-slate-200/80 rounded-2xl p-5 hover:border-slate-300 hover:shadow-md transition-all duration-200"
                >
                  <div className="h-10 flex items-center justify-start mb-4">
                    <img src={link.logo} alt={link.title} className="h-full object-contain transition" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 group-hover:text-black flex items-center gap-1 transition">
                    {link.title}
                    <ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 transition" />
                  </h3>
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                    {link.description}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
